import React, { useState } from 'react';
import { useAppContext, useJourneyContext } from 'Contexts';
import { BreakdownSteps, PaypalLegalNotices, PipeSteps, Popin, RegistrationLegalNotices } from 'Components';
import { Action, AutocompletionService, getCustomAction, TrackingService } from 'Services';
import { useDeviceDetect } from 'Hooks';
import { FRStep } from '@forgerock/javascript-sdk';
import { Origin } from 'Constants';
import { isInvalidPostalAddress, POSTAL_ADDRESS_INVALID_MESSAGE } from 'Services/postalAddressValidation';

const capitalize = (text: string) => text.toLowerCase().replace(/\b./g, (letter: string) => letter.toUpperCase());
const getInputValue = (formElements: HTMLFormControlsCollection, inputName: string) =>
  (formElements.namedItem(inputName) as HTMLInputElement).value;

export const Page: React.FC = () => {
  const { origin, breadcrumb, eligibleToShortcutPaypal, currentBreakdownStep } = useAppContext();
  const { goToNextStep, components, headers, isAtStage, step, isStandardSubmit } = useJourneyContext();
  const [popinOpened, setPopinOpened] = useState(false);
  const [badAddress, setBadAddress] = useState<string>();
  const [registrationFailureTracked, setRegistrationFailureTracked] = useState(false);
  const { mobile } = useDeviceDetect();

  const isSubmitWithEmptyEmail = (event: React.FormEvent<HTMLFormElement>) =>
    isStandardSubmit && (event.currentTarget.elements.namedItem('mail') as HTMLInputElement)?.value === '';

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (isAtStage('Registration Page')) {
      const formElements = event.currentTarget.elements;

      const postalAddress = getInputValue(formElements, 'postalAddress');
      const postalCode = getInputValue(formElements, 'postalCode');
      const city = getInputValue(formElements, 'city');

      if (isInvalidPostalAddress(postalAddress)) {
        const addressInput = formElements.namedItem('postalAddress') as HTMLInputElement | null;
        if (addressInput) {
          addressInput.setCustomValidity(POSTAL_ADDRESS_INVALID_MESSAGE);
          addressInput.reportValidity();
        }
        return;
      }

      AutocompletionService.validateAddress(postalAddress, postalCode, city).then((addressIsValid) => {
        if (addressIsValid) {
          goToNextStep();
        } else {
          setBadAddress(`${capitalize(postalAddress)}\n${postalCode} ${capitalize(city)}`);
          setPopinOpened(true);
        }
      });

      return;
    } else if (isAtStage('Connexion / Registration Page') && isSubmitWithEmptyEmail(event)) {
      return;
    }

    goToNextStep();
  };

  const trackPopin = (action: Action, step?: FRStep) => {
    const stage = step?.getStage() as Stage;
    const customAction = getCustomAction(action, stage);
    if (!customAction || !origin) return;

    const eventId = TrackingService.buildEventIdClick(stage, customAction, origin);
    const path = TrackingService.buildPath(stage, mobile, origin);
    TrackingService.triggerTrackingClic(eventId, path);
  };

  const handleClosePopin = () => {
    setPopinOpened(false);
    trackPopin('failure', step);
  };

  const handleConfirmPopin = () => {
    setPopinOpened(false);
    goToNextStep();
    trackPopin('success', step);
  };

  const handleOpenPopin = () => trackPopin('popin', step);

  const handleInvalid = () => {
    const stage = 'Registration Page';
    if (!isAtStage(stage) || registrationFailureTracked || !origin) return;

    const path = TrackingService.buildPath(stage, mobile, origin);
    const eventId = TrackingService.buildEventIdClick(stage, 'failure', origin);
    TrackingService.triggerTrackingClic(eventId, path);
    setRegistrationFailureTracked(true);
  };

  const handleChange = () => {
    setRegistrationFailureTracked(false);
  };

  const isPipe = () => origin === Origin.PIPE;
  const isBreakdownTunnel = () => origin === Origin.BREAKDOWN;
  const withBreadcrumb = isBreakdownTunnel() || (isPipe() && breadcrumb);

  return (
    <>
      <div className="flex flex-col pt-6 md:pt-0 flex-1 items-center md:max-w-[1012px] md:px-4 w-full m-auto">
        {isPipe() && breadcrumb && <PipeSteps />}
        {isBreakdownTunnel() && <BreakdownSteps currentStep={currentBreakdownStep} />}
        <div className="px-4 md:w-[598px]">
          <div
            className={`flex-1 md:px-12 p-4 md:py-10 md:border-whitesmoke md:border md:rounded mb-12 bg-white ${
              withBreadcrumb ? '' : 'md:my-16'
            }`}
          >
            {headers.image && <img src={headers.image.src} alt="" className={headers.image.className} />}

            <h1 className="font-bold text-dark-gray text-xl mb-3 flex flex-row items-stretch gap-2">
              <span>{headers.title.text}</span>
              {headers.title.icon && <img className="h-5 w-5 self-center" src={headers.title.icon} alt={headers.title.icon} />}
            </h1>

            <div className="text-sonic-silver text-sm mb-3">{headers.subTitle}</div>

            <form className="flex flex-col gap-5" onSubmit={handleSubmit} onInvalid={handleInvalid} onChange={handleChange}>
              {components}
            </form>

            {popinOpened && (
              <Popin
                title="L’adresse est-elle bien renseignée ?"
                cancelTitle="Corriger l’adresse"
                onCancelClick={handleClosePopin}
                onCloseClick={handleClosePopin}
                confirmTitle="Valider l’adresse"
                onConfirmClick={handleConfirmPopin}
                onLoad={handleOpenPopin}
                popinWidth="md:w-[530px]"
              >
                <span className="text-xs">L’adresse renseignée nous est inconnue :</span>
                <pre className="font-bold my-3 text-xs">{badAddress}</pre>
                <span className="text-xs">Voulez-vous la corriger ou la garder telle quelle&nbsp;?</span>
              </Popin>
            )}
          </div>
        </div>
      </div>
      {isAtStage('Connexion / Registration Page') && eligibleToShortcutPaypal && <PaypalLegalNotices />}
      {isAtStage('Registration Page') && <RegistrationLegalNotices />}
    </>
  );
};
